import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faUser } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import { useAuth } from "../../contexts/AuthContext";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import Loading from "../../Components/Common/Loading";
import PageHeader from "../../Components/Common/PageHeader";

const ProfilePage = () => {
  const { currentUser } = useAuth();
  const currentPasswordRef = useRef(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordErr, setCurrentPasswordErr] = useState("");
  const [newPasswordErr, setNewPasswordErr] = useState("");
  const [confirmPasswordErr, setConfirmPasswordErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Utils.setPageTitle("Profile");
    currentPasswordRef.current?.focus();
  }, []);

  const clearFieldError = (field) => {
    if (field === "currentPassword") {
      setCurrentPasswordErr("");
      return;
    }

    if (field === "newPassword") {
      setNewPasswordErr("");
      return;
    }

    setConfirmPasswordErr("");
  };

  const validateForm = () => {
    let valid = true;

    if (!currentPassword.length) {
      setCurrentPasswordErr("This field is required!");
      valid = false;
    }

    if (!newPassword.length) {
      setNewPasswordErr("This field is required!");
      valid = false;
    } else if (!Enums.Regex.PASS.test(newPassword)) {
      setNewPasswordErr("Please enter a stronger password!");
      valid = false;
    } else if (newPassword === currentPassword) {
      setNewPasswordErr("New password must be different from current password!");
      valid = false;
    }

    if (!confirmPassword.length) {
      setConfirmPasswordErr("This field is required!");
      valid = false;
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordErr("Passwords do not match!");
      valid = false;
    }

    return valid;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        currentPassword,
        oldPassword: currentPassword,
        newPassword,
        password: newPassword,
      };

      const data = await Utils._apiRequest({
        path: Enums.endpoints.auth.CHANGE_PASSWORD,
        type: "POST",
        payload,
      });

      if (data?.errors) {
        if (data.errors.currentPassword || data.errors.oldPassword) {
          setCurrentPasswordErr(
            data.errors.currentPassword || data.errors.oldPassword,
          );
        }

        if (data.errors.newPassword || data.errors.password) {
          setNewPasswordErr(data.errors.newPassword || data.errors.password);
        }

        if (data.errors.confirmPassword) {
          setConfirmPasswordErr(data.errors.confirmPassword);
        }

        if (data.errors.message) {
          toast.error(data.errors.message);
        }

        return;
      }

      if (data?.message) {
        toast.success(data.message);
      } else {
        toast.success("Password changed successfully.");
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page-main">
      <div className="main-container container">
        <PageHeader
          title="Profile"
          description="Account details and password security"
          icon={faUser}
        />

        <Row className="g-3">
          <Col lg={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <h6 className="border-bottom pb-2 mb-3">Account information</h6>
                <div className="mb-2">
                  <small className="text-muted d-block">Full name</small>
                  <strong>{currentUser?.fullName || "N/A"}</strong>
                </div>
                <div className="mb-2">
                  <small className="text-muted d-block">Email</small>
                  <strong>{currentUser?.email || "N/A"}</strong>
                </div>
                <div>
                  <small className="text-muted d-block">Status</small>
                  <strong>{currentUser?.status || "N/A"}</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="border-0 shadow-sm overflow-hidden">
              <Card.Body className="position-relative">
                <Loading show={loading} />
                <h6 className="border-bottom pb-2 mb-3">
                  <FontAwesomeIcon icon={faKey} className="me-2" />
                  Change password
                </h6>
                <Form onSubmit={handleChangePassword}>
                  <FloatingLabel
                    controlId="currentPasswordInput"
                    label="Current password"
                    className="mb-3"
                  >
                    <Form.Control
                      ref={currentPasswordRef}
                      type="password"
                      value={currentPassword}
                      className={currentPasswordErr ? "is-invalid" : ""}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        clearFieldError("currentPassword");
                      }}
                      placeholder="Current password"
                    />
                    {currentPasswordErr ? (
                      <p className="invalid-feedback">{currentPasswordErr}</p>
                    ) : null}
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="newPasswordInput"
                    label="New password"
                    className="mb-3"
                  >
                    <Form.Control
                      type="password"
                      value={newPassword}
                      className={newPasswordErr ? "is-invalid" : ""}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        clearFieldError("newPassword");
                      }}
                      placeholder="New password"
                    />
                    {newPasswordErr ? (
                      <p className="invalid-feedback">{newPasswordErr}</p>
                    ) : null}
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="confirmPasswordInput"
                    label="Confirm new password"
                    className="mb-3"
                  >
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      className={confirmPasswordErr ? "is-invalid" : ""}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        clearFieldError("confirmPassword");
                      }}
                      placeholder="Confirm new password"
                    />
                    {confirmPasswordErr ? (
                      <p className="invalid-feedback">{confirmPasswordErr}</p>
                    ) : null}
                  </FloatingLabel>

                  <p className="text-muted small mb-3">
                    Password must contain uppercase, lowercase, number and one of
                    these symbols: ! @ # $ %
                  </p>

                  <div className="text-end">
                    <Button type="submit" variant="primary" disabled={loading}>
                      Save new password
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </main>
  );
};

export default ProfilePage;